import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationFooterProps {
    /** Number of rows currently known to the client. */
    totalRows: number;
    /** Zero-based page index. */
    page: number;
    pageSize: number;
    onPrevious: () => void;
    onNext: () => void;
    hasPrevious?: boolean;
    hasNext?: boolean;
    /** Cursor-backed collections may know there are more rows without knowing the final total. */
    totalIsExact?: boolean;
    isLoadingNext?: boolean;
    /** Override the derived range for cursor pages whose filtered lengths vary. */
    rangeStart?: number;
    rangeEnd?: number;
}

/**
 * The pinned collection footer shared by Inbox and the app's other list views.
 * Its layout mirrors Attend AAA's CollectionView footer while supporting both
 * fully-loaded client collections and cursor-backed server collections.
 */
export function PaginationFooter({
    totalRows,
    page,
    pageSize,
    onPrevious,
    onNext,
    hasPrevious = page > 0,
    hasNext = (page + 1) * pageSize < totalRows,
    totalIsExact = true,
    isLoadingNext = false,
    rangeStart,
    rangeEnd,
}: PaginationFooterProps) {
    const start = rangeStart ?? (totalRows === 0 ? 0 : page * pageSize + 1);
    const end = rangeEnd ?? Math.min(totalRows, (page + 1) * pageSize);
    const suffix = totalIsExact ? "" : "+";
    const rowLabel = totalRows === 1 && totalIsExact ? "row" : "rows";

    return (
        <footer className="pagination-footer" aria-label="Pagination">
            <span className="pagination-footer-count">
                {totalRows}{suffix} {rowLabel}
            </span>
            <div className="pagination-footer-controls">
                <span className="pagination-footer-range">
                    {start === 0 ? "0" : `${start}–${end}`} of {totalRows}{suffix}
                </span>
                <button
                    type="button"
                    onClick={onPrevious}
                    disabled={!hasPrevious}
                    aria-label="Previous page"
                    title="Previous page"
                >
                    <ChevronLeft />
                </button>
                <button
                    type="button"
                    onClick={onNext}
                    disabled={!hasNext || isLoadingNext}
                    aria-label={isLoadingNext ? "Loading next page" : "Next page"}
                    title="Next page"
                >
                    <ChevronRight />
                </button>
            </div>
        </footer>
    );
}
